import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LeagueMembershipContext } from "./LeagueMembershipContext";
import { useAuth } from "./AuthContext";
import { useLeague } from "../hooks/useLeague";
import { getCurrentLeagueUserUrl } from "../config/api";
import { apiGet } from "../utils/apiClient";
import { canManageLeague, canScoreGames } from "../utils/leagueRoles";
import { LeagueUser } from "../types/models";

interface MembershipResult {
  /** The user, league, and refresh this result was loaded for. */
  requestKey: string;
  membership?: LeagueUser;
  error?: Error;
}

/**
 * Loads the signed-in user's membership in the selected league so the UI only
 * offers the actions their role allows. The API enforces the same rules.
 *
 * A result is exposed only while it still matches the current session and
 * league, so switching either never briefly grants the previous membership's
 * permissions.
 */
function LeagueMembershipProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const { currentLeague } = useLeague();
  const leagueId = currentLeague?.id;
  const leagueAbbr = currentLeague?.abbr;
  const [refreshCount, setRefreshCount] = useState(0);
  const [result, setResult] = useState<MembershipResult | null>(null);

  const requestKey =
    token && leagueId && leagueAbbr
      ? `${token}:${leagueId}:${refreshCount}`
      : null;

  useEffect(() => {
    if (!requestKey || !leagueId || !leagueAbbr) return;

    let cancelled = false;

    apiGet<LeagueUser>(getCurrentLeagueUserUrl(leagueId), {
      token,
      leagueAbbr,
    })
      .then((membership) => {
        if (!cancelled) setResult({ requestKey, membership });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setResult({
          requestKey,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey, token, leagueId, leagueAbbr]);

  const refreshMembership = useCallback(() => {
    setRefreshCount((count) => count + 1);
  }, []);

  const value = useMemo(() => {
    const current = result?.requestKey === requestKey ? result : null;
    const role = current?.membership?.role;

    return {
      membership: current?.membership,
      role,
      canManageLeague: canManageLeague(role),
      canScoreGames: canScoreGames(role),
      isLoading: requestKey !== null && current === null,
      error: current?.error,
      refreshMembership,
    };
  }, [result, requestKey, refreshMembership]);

  return (
    <LeagueMembershipContext.Provider value={value}>
      {children}
    </LeagueMembershipContext.Provider>
  );
}

export default LeagueMembershipProvider;
